import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';

import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Subject } from 'rxjs';
import { BaseConditionData } from 'src/app/components/designer/models/base-condition-data';
import { LogicalExpressionConditionData } from 'src/app/components/designer/models/logical-expression-condition-data';
import { FormMode } from 'src/app/controls/common/form-mode';
import { ContextualMenuEvent } from 'src/app/controls/workflows/contextual-menu-event';
import { NeedConditions } from 'src/app/controls/workflows/need-conditions';
import { WorkflowDataInterface } from 'src/app/controls/workflows/workflow-data.interface';

@Component({
    selector: 'app-workflow-condition-logical-expression-data',
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, FaIconComponent, CommonModule, MatTooltipModule],
    templateUrl: './workflow-condition-logical-expression-data.component.html',
    styleUrl: './workflow-condition-logical-expression-data.component.scss'
})
export class WorkflowConditionLogicalExpressionDataComponent implements WorkflowDataInterface, OnInit, NeedConditions {
    /**
     * Liste des icones utilisés dans le template.
     */
    public faCircleInfo = faCircleInfo;

    /**
     * Données affichées par le composant dynamique.
     */
    public _data: LogicalExpressionConditionData;

    /**
     * Spécifie les données affichées par le composant dynamique.
     */
    set data(value: string | LogicalExpressionConditionData) {
        if (typeof value === 'string') {
            this._data = JSON.parse(value);
        } else {
            this._data = value as LogicalExpressionConditionData;
        }

        this.fillForm();
    }

    /**
     * Obtient les données affichées par le composant dynamique.
     */
    get data(): LogicalExpressionConditionData {
        this.fillData();
        return this._data;
    }

    /**
     * Mode d'affichage du formulaire.
     */
    mode: FormMode;

    /**
     * Source du menu contextuel.
     */
    contextualMenuSource: Subject<ContextualMenuEvent> = new Subject<ContextualMenuEvent>();

    /**
     * Liste des conditions.
     */
    public _conditions: BaseConditionData[];

    /**
     * Spécifie la liste des conditions.
     */
    set conditions(value: BaseConditionData[]) {
        this._conditions = value;
        this.fillForm();
    }

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    /**
     * Message d'erreur affiché par le validateur lorsque l'expression booléenne est invalide.
     */
    invalidErrorMessage: string = null;

    /**
     * Constructeur.
     */
    constructor() {
        console.log('WorkflowConditionLogicalExpressionDataComponent - constructor : ');
    }

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        console.log('WorkflowConditionLogicalExpressionDataComponent - ngOnInit : ');
        this.dataForm = new UntypedFormGroup({
            expression: new UntypedFormControl([], [Validators.required]),
        });

        this.fillForm();
    }

    /**
     * Indique si le formulaire est valide.
     */
    public isValid(): boolean {
        return this.dataForm.valid;
    }

    /**
     * Retourne une valeur indiquant si l'utilisateur a modifié des valeurs du formulaire.
     */
    public get dirty(): boolean {
        return this.dataForm && this.dataForm.dirty;
    }

    /**
     * Levé lorsque l'utilisateur fait un clic-droit sur un composant Input.
     * @param source : Identifiant du composant. Permet de déterminer dans quel composant le paramètre sera ajouté.
     * @param event : Événement souris.
     */
    public onInputRightClick(source: string, event: MouseEvent) {
        this.contextualMenuSource.next(new ContextualMenuEvent(this.dataForm.get(source), event));
    }

    /**
     * Annulle les changements effectués par l'utilisateur.
     */
    public clearData() {
        this.dataForm.markAsPristine();
    }

    /**
     * Remplit le formulaire avec les données.
     */
    private fillForm() {
        if (this.dataForm) {
            if (this._data) {
                this.dataForm.get('expression').setValue(this._data.expression);
            }

            if (this._conditions) {
                this.dataForm.controls['expression'].addValidators(isLogicalExpression(this._conditions));
            }
        }
    }

    /**
     * Met à jour les données à partir du formulaire.
     */
    private fillData() {
        let updatedData: LogicalExpressionConditionData = new LogicalExpressionConditionData();
        updatedData.expression = this.dataForm.get('expression').value;

        this._data = updatedData;
    }
}

/**
 * Fonction permettant de valider que la valeur d'un champ est une expression logique.
 * @param conditions : Liste des conditions référencées.
 * @returns Une erreur si la valeur du champ n'est pas une expression logique; null autrement.
 */
export function isLogicalExpression(conditions: BaseConditionData[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (typeof control.value !== 'string') {
            return { invalidError: true };
        }

        try {
            ParseExpression(control.value, conditions);
        } catch (ex) {
            console.error(ex);
            return { invalidError: ex.message };
        }

        return null;
    };
}

/**
 * Parse l'expression
 * @param expression : Expression booléenne.
 * @param conditions Liste des conditions référencées.
 * @returns
 */
export function ParseExpression(expression: string, conditions: BaseConditionData[]) {
    if (!expression || expression.length === 0) {
        return null;
    }

    let tokens: string[] = [];
    let sb: string = '';

    for (let i = 0; i < expression.length; i++) {
        let c = expression[i];
        switch (c) {
            case ' ':
            case '\t':
            case '\r':
            case '\n':
                if (sb.length == 0) continue;
                sb = sb.concat(c);
                break;
            case '(':
            case ')':
            case '^':
            case '!':
                if (sb.length > 0) {
                    tokens.push(sb);
                    sb = '';
                }

                tokens.push(c);
                break;
            case '&':
            case '|':
                if (sb.length != 0) {
                    let prev = sb[sb.length - 1];
                    if (c == prev) {
                        // && or ||
                        sb = sb.substring(0, sb.length - 1); // remove last char

                        if (sb.trim().length != 0) {
                            tokens.push(sb.trim());
                        }
                        sb = '';

                        tokens.push(c == '&' ? '&&' : '||');
                        break;
                    }
                }

                sb = sb.concat(c);
                break;

            default:
                sb = sb.concat(c);
                break;
        }
    }

    if (sb.length > 0) {
        tokens.push(sb);
    }

    return Parse(tokens, conditions);
}

/**
 * Parse les tokens constitutifs de l'expression régulière
 * @param tokens : Liste des tokens.
 * @param conditions : Liste des conditions référencées.
 */
export function Parse(tokens: string[], conditions: BaseConditionData[]) {
    let index = 0;
    ParseExp(tokens, index, conditions);
}

/**
 * Parse un des tokens constitutifs de l'expression régulière.
 * @param tokens : Liste des tokens.
 * @param index : Index du token.
 * @param conditions : Liste des conditions référencées.
 * @returns Index du prochain token à analyser.
 */
export function ParseExp(tokens: string[], index: number, conditions: BaseConditionData[]) {
    index = ParseSubExp(tokens, index, conditions);
    if (index >= tokens.length) return index;

    if (tokens[index] == ')') return index;

    let token: string = tokens[index];

    if (token == '&&') {
        index++;
        index = ParseExp(tokens, index, conditions);
        return index;
    } else if (token == '||') {
        index++;
        index = ParseExp(tokens, index, conditions);
        return index;
    } else if (token == '^') {
        index++;
        index = ParseExp(tokens, index, conditions);
        return index;
    } else {
        throw new Error("L'opérateur n'est pas correct (Opérateurs autorisés : '&&' ou '||' ou '^'");
    }
}

/**
 * Parse un des tokens constitutifs de l'expression régulière correspondant à un opérande.
 * @param tokens : Liste des tokens.
 * @param index : Index du token.
 * @param conditions : Liste des conditions référencées.
 * @returns Index du prochain token à analyser.
 */
export function ParseSubExp(tokens: string[], index: number, conditions: BaseConditionData[]): number {
    let token: string = tokens[index];

    if (token == '(') {
        index++;
        index = ParseExp(tokens, index, conditions);

        if (tokens[index] != ')') throw new Error("')' attendue");

        index++; // Skip ')'

        return index;
    } else if (token == '!') {
        index++;
        index = ParseSubExp(tokens, index, conditions);
        return index;
    } else {
        const regex = new RegExp(/^C(\d+)$/i);
        if (token && token.length > 0) {
            if (!regex.test(token)) {
                throw new Error("L'opérande ne correspond pas à une condition");
            } else {
                let matches = regex.exec(token);
                if (!conditions.find((x) => x.id === +matches[1])) {
                    throw new Error("L'opérande ne correspond pas à une condition existante");
                }
            }
        } else {
            throw new Error('Opérande manquant');
        }

        index++;
        return index;
    }
}
